export interface LanguageConfig {
  code: string;
  name: string;      // English name
  localName: string; // Native name
  dir: 'ltr' | 'rtl';
  ogLocale: string;
}

export const LANGUAGES: LanguageConfig[] = [
  { code: 'en', name: 'English', localName: 'English', dir: 'ltr', ogLocale: 'en_US' },
  { code: 'zh', name: 'Chinese', localName: '中文', dir: 'ltr', ogLocale: 'zh_CN' },
  { code: 'hi', name: 'Hindi', localName: 'हिन्दी', dir: 'ltr', ogLocale: 'hi_IN' },
  { code: 'de', name: 'German', localName: 'Deutsch', dir: 'ltr', ogLocale: 'de_DE' },
  { code: 'es', name: 'Spanish', localName: 'Español', dir: 'ltr', ogLocale: 'es_ES' },
  { code: 'ru', name: 'Russian', localName: 'Русский', dir: 'ltr', ogLocale: 'ru_RU' },
  { code: 'ja', name: 'Japanese', localName: '日本語', dir: 'ltr', ogLocale: 'ja_JP' },
  { code: 'ko', name: 'Korean', localName: '한국어', dir: 'ltr', ogLocale: 'ko_KR' },
  { code: 'ar', name: 'Arabic', localName: 'العربية', dir: 'rtl', ogLocale: 'ar_SA' },
  { code: 'fa', name: 'Persian', localName: 'فارسی', dir: 'rtl', ogLocale: 'fa_IR' },
  { code: 'pt', name: 'Portuguese', localName: 'Português', dir: 'ltr', ogLocale: 'pt_PT' },
  { code: 'id', name: 'Indonesian', localName: 'Bahasa Indonesia', dir: 'ltr', ogLocale: 'id_ID' }
];

export const DEFAULT_LANGUAGE = 'en';
export const RTL_LANGUAGES = ['ar', 'fa'];
export const SUPPORTED_LANGUAGES = LANGUAGES.map((lang) => lang.code);

export function isRTL(lang: string): boolean {
  return RTL_LANGUAGES.includes(lang);
}

export function getLanguage(code: string): LanguageConfig {
  return LANGUAGES.find((lang) => lang.code === code) || LANGUAGES[0];
}

/**
 * Extract the language code from a pathname.
 * Returns DEFAULT_LANGUAGE ('en') if the first segment is not a supported language.
 */
export function getLangFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] || '';
  return SUPPORTED_LANGUAGES.includes(firstSegment) ? firstSegment : DEFAULT_LANGUAGE;
}

/**
 * Remove the language prefix from a pathname.
 * '/zh/products' -> '/products'
 * '/products'     -> '/products'
 */
export function stripLangFromPath(pathname: string): string {
  const lang = getLangFromPath(pathname);
  if (lang === DEFAULT_LANGUAGE) return pathname || '/';
  return pathname.replace(`/${lang}`, '') || '/';
}

/**
 * Build a localized path for a given language.
 * localizePath('/products', 'zh') -> '/zh/products'
 * localizePath('/products', 'en') -> '/products'
 */
export function localizePath(path: string, lang: string): string {
  const cleanPath = stripLangFromPath(path);
  if (lang === DEFAULT_LANGUAGE) return cleanPath;
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
}

/**
 * Build the full canonical URL for a path in a given language.
 */
export function localizeUrl(path: string, lang: string, domain = 'gyronavi.com'): string {
  return `https://${domain}${localizePath(path, lang)}`;
}
